/**
 * KanbanColumn Component
 * 
 * Componente per una singola colonna del Kanban board
 * Supporta drop zone per i task draggati
 */

import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  Box,
  Stack,
  Title,
  Badge,
  Paper,
  ScrollArea,
  Group,
  ActionIcon,
  Menu,
  Text,
} from '@mantine/core';
import {
  IconDotsVertical,
  IconPlus,
  IconSettings,
  IconFilter,
} from '@tabler/icons-react';

import { KanbanColumn as KanbanColumnType } from '../../types/api';
import { kanbanService } from '../../services/kanbanService';
import KanbanCard from './KanbanCard';

interface KanbanColumnProps {
  column: {
    id: KanbanColumnType;
    title: string;
    tasks: any[]; // Task[]
  };
  onTaskClick: (taskId: number) => void;
  onAddTask?: (columnId: KanbanColumnType) => void;
}

const KanbanColumnComponent: React.FC<KanbanColumnProps> = ({
  column,
  onTaskClick,
  onAddTask,
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id: String(column.id),
  });

  const columnColor = kanbanService.getColumnColor(column.id);
  
  return (
    <Box
      ref={setNodeRef}
      style={{
        minWidth: '300px',
        maxWidth: '350px',
        width: '100%',
        height: '100%',
      }}
    >
      <Paper
        p="sm"
        style={{
          height: '100%',
          border: isOver ? `2px dashed ${columnColor}` : '1px solid #e9ecef',
          backgroundColor: isOver ? `${columnColor}10` : 'white',
          transition: 'all 0.2s ease',
        }}
        withBorder
      >
        {/* Header della colonna */}
        <Group justify="space-between" mb="sm">
          <Group>
            <Box
              style={{
                width: '4px',
                height: '20px',
                backgroundColor: columnColor,
                borderRadius: '2px',
              }}
            />
            <Title order={5} style={{ color: columnColor }}>
              {column.title}
            </Title>
            <Badge
              variant="filled"
              color="gray"
              size="sm"
              style={{ color: 'white' }}
            >
              {column.tasks.length}
            </Badge>
          </Group>

          <Menu>
            <Menu.Target>
              <ActionIcon variant="subtle" size="sm">
                <IconDotsVertical size={14} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              {onAddTask && (
                <Menu.Item 
                  leftSection={<IconPlus size={16} />}
                  onClick={() => onAddTask(column.id)}
                >
                  Aggiungi task
                </Menu.Item>
              )}
              <Menu.Item leftSection={<IconFilter size={16} />}>
                Filtra colonna
              </Menu.Item>
              <Menu.Item leftSection={<IconSettings size={16} />}>
                Impostazioni
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>

        {/* Lista dei task */}
        <ScrollArea 
          style={{ 
            height: 'calc(100% - 60px)',
          }}
          scrollbarSize={8}
        >
          <SortableContext
            items={column.tasks.map(task => String(task.id))}
            strategy={verticalListSortingStrategy}
          >
            <Stack gap="sm">
              {column.tasks.length === 0 ? (
                <Paper
                  p="xl"
                  style={{
                    textAlign: 'center',
                    border: '2px dashed #e9ecef',
                    backgroundColor: '#f8f9fa',
                  }}
                >
                  <Text c="dimmed" size="sm">
                    Nessun task in questa colonna
                  </Text>
                  <Text c="dimmed" size="xs" mt={4}>
                    Trascina qui i task o creane di nuovi
                  </Text>
                </Paper>
              ) : (
                column.tasks.map((task) => (
                  <KanbanCard
                    key={task.id}
                    task={task}
                    onClick={() => onTaskClick(task.id)}
                  />
                ))
              )}
            </Stack>
          </SortableContext>
        </ScrollArea>
      </Paper>
    </Box>
  );
};

export default KanbanColumnComponent;